// =============================================================================
// src/chrome/copilot-sidebar/tactic-cards-by-section.ts
// Dataset das "Oportunidades táticas — Específicas desta vista" do Copiloto.
//
// Proveniencia: porte fiel de assets/unified-app.js linhas 2637-2682
//   (CopilotTactics.data). Cada secao scrollavel mapeia para 0..N cards taticos;
//   secoes vazias ([]) escondem o bloco (classe is-empty no controller).
//   Textos/percentuais/CTAs mantidos verbatim para paridade visual.
//
// SRP: este arquivo so carrega os dados. O markup do card vive em
//   tactic-card-markup.ts; o controller que renderiza vive em
//   tactics-controller.ts.
// =============================================================================

import type { SectionId } from '../../app/types';

/** Prioridade tatica (chave de cor/rotulo do card). */
export type TacticPriority = 'alta' | 'media' | 'baixa';

/**
 * Um card tatico especifico de uma vista (CopilotTactics.data[*][n]).
 * `ctaGhost` opcional troca o CTA para a variante secundaria (--ghost).
 */
export interface ViewTacticCard {
  readonly priority: TacticPriority;
  readonly category: string;
  readonly confidence: number;
  readonly desc: string;
  readonly impact: string;
  readonly cta: string;
  readonly ctaGhost?: boolean;
}

/** Táticas específicas por secao (CopilotTactics.data). Secoes vazias = []. */
export const TACTIC_CARDS_BY_SECTION: Record<SectionId, readonly ViewTacticCard[]> = {
  'mapa-eleitoral': [],
  'perfil-regiao': [
    {
      priority: 'alta',
      category: 'Visibilidade',
      confidence: 76,
      desc: 'Concentrar comício no eixo Cidade Nova / Nova Cidade neste sábado',
      impact: '+5.4% pertencimento',
      cta: 'Ativar tática',
    },
    {
      priority: 'media',
      category: 'Comunicação',
      confidence: 64,
      desc: 'Mensagem segmentada no vácuo digital jovem (1º voto)',
      impact: 'Alcance +12%',
      cta: 'Ver detalhes',
      ctaGhost: true,
    },
  ],
  comparador: [
    {
      priority: 'alta',
      category: 'Coligação',
      confidence: 79,
      desc: 'Atacar overlap PSD/União Brasil no centro-direita após colapso do PSD',
      impact: 'Transferência +2.1k votos',
      cta: 'Ativar tática',
    },
  ],
  demografica: [
    {
      priority: 'media',
      category: 'Mobilização',
      confidence: 67,
      desc: 'Porta-a-porta no recorte feminino 35-49 da zona leste',
      impact: '+1.4k votos potenciais',
      cta: 'Ativar tática',
    },
    {
      priority: 'baixa',
      category: 'Comunicação',
      confidence: 58,
      desc: 'Conteúdo educacional para faixa 60+ via WhatsApp/rádio',
      impact: 'Alcance +8%',
      cta: 'Ver detalhes',
      ctaGhost: true,
    },
  ],
  camadas: [
    {
      priority: 'media',
      category: 'Visibilidade',
      confidence: 72,
      desc: 'Cruzar camada saneamento + renda baixa para pauta de campanha',
      impact: 'Cobertura +18%',
      cta: 'Ativar tática',
    },
  ],
  negocios: [
    {
      priority: 'media',
      category: 'Coligação',
      confidence: 69,
      desc: 'Aproximação com associações comerciais do varejo de bairro',
      impact: 'Apoio +6 lideranças',
      cta: 'Ativar tática',
    },
  ],
  grafo: [
    {
      priority: 'alta',
      category: 'Mobilização',
      confidence: 81,
      desc: 'Mobilizar 4 hubs de influência identificados na zona norte',
      impact: 'Cascata +9.3k pessoas',
      cta: 'Ativar tática',
    },
  ],
  impostometro: [],
  monitoramento: [
    {
      priority: 'alta',
      category: 'Comunicação',
      confidence: 88,
      desc: 'Resposta rápida ao evento da Av. Djalma — janela de 6h',
      impact: 'Controle de narrativa',
      cta: 'Ativar tática',
    },
  ],
  cinema: [],
};

/** Base fixa de táticas prioritárias (CopilotTactics.baseTacticsCount = 3). */
export const BASE_TACTICS_COUNT = 3;
